import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, CheckCircle2, Loader2, AlertCircle, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const DOC_LABELS = { visura: "Visura camerale", id_card: "Documento identità", other: "Altro documento" };

export default function AgencyOnboarding() {
  const { token } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({
    legal_name: "", vat_number: "", fiscal_code: "",
    address: "", cap: "", city: "", province: "",
    phone: "", pec_email: "", rui_number: "", iban: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null); // doc_type currently uploading
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    axios.get(`${apiBase}/public/agency-onboarding/${token}`)
      .then((res) => {
        setInfo(res.data);
        setForm((f) => ({
          ...f,
          legal_name: res.data?.onboarding_data?.legal_name || res.data.agency_name || "",
          phone: res.data?.onboarding_data?.phone || res.data.owner_phone || "",
          ...res.data?.onboarding_data,
        }));
        setDocs(res.data?.onboarding_data?.documents || []);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Link non valido"))
      .finally(() => setLoading(false));
  }, [token]);

  const uploadDoc = async (file, doc_type) => {
    if (!file) return;
    setUploading(doc_type);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", doc_type);
      const { data } = await axios.post(`${apiBase}/public/agency-onboarding/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((d) => [...d, { id: Math.random().toString(36), doc_type, filename: file.name, url: data.url, uploaded_at: new Date().toISOString() }]);
      toast.success(`${DOC_LABELS[doc_type]} caricato`);
    } catch (e) {
      toast.error(`Errore upload: ${formatApiError(e)}`);
    } finally { setUploading(null); }
  };

  const submit = async () => {
    if (!form.legal_name.trim()) { toast.error("Ragione sociale obbligatoria"); return; }
    setBusy(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      // 🐛 30/07/2026 — Rimuovo campi vuoti dal payload per evitare 422 su EmailStr (pec_email)
      // e per non salvare stringhe vuote come dati anagrafici.
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
                            .filter(([, v]) => v !== "" && v != null),
      );
      // legal_name è obbligatorio: lo re-includo se filtrato per sicurezza
      payload.legal_name = form.legal_name.trim();
      await axios.post(`${apiBase}/public/agency-onboarding/${token}`, payload);
      toast.success("✅ Dati inviati correttamente");
      setDone(true);
    } catch (e) {
      toast.error(`Errore invio: ${formatApiError(e)}`);
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">
      <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…
    </div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold mb-2">Link non valido</h2>
            <p className="text-sm text-slate-600 mb-4">{error}</p>
            <Button onClick={() => nav("/login")} variant="outline">Vai al login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50 p-6">
        <Card className="max-w-md w-full" data-testid="onboarding-success">
          <CardContent className="pt-8 pb-6 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold mb-2">Dati inviati!</h2>
            <p className="text-sm text-slate-600 mb-4">
              Grazie. L&apos;amministratore verificherà i dati e attiverà la tua agenzia entro 24h. Riceverai un&apos;email di conferma.
            </p>
            <Button onClick={() => nav("/login")} className="w-full">Vai al login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-10 px-6">
      <Card className="max-w-2xl mx-auto" data-testid="onboarding-form">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-blue-600" /> Completa i dati di <span className="text-blue-700">{info?.agency_name}</span></CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Inserisci i dati completi della tua agenzia. Verranno verificati e poi useremo per attivare il tuo workspace.
            {info?.already_submitted && <span className="block mt-1 text-amber-600">⚠️ Hai già inviato i dati. Puoi aggiornarli prima dell&apos;attivazione.</span>}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Ragione sociale completa <span className="text-red-500">*</span></Label>
            <Input data-testid="ob-legal-name" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="Es. Studio di Consulenza Rossi SRL" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>P.IVA</Label>
              <Input data-testid="ob-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="01234567890" />
            </div>
            <div>
              <Label>Codice fiscale</Label>
              <Input data-testid="ob-fc" value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} />
            </div>
          </div>
          <div>
            <Label>Indirizzo sede</Label>
            <Input data-testid="ob-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Via Roma 10" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label>CAP</Label>
              <Input data-testid="ob-cap" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value.replace(/\D/g, "").slice(0, 5) })} />
            </div>
            <div>
              <Label>Città</Label>
              <Input data-testid="ob-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <Label>Prov.</Label>
              <Input data-testid="ob-province" maxLength={2} value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase().slice(0, 2) })} placeholder="RM" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Telefono fisso</Label>
              <Input data-testid="ob-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12345678" />
            </div>
            <div>
              <Label>Email PEC</Label>
              <Input data-testid="ob-pec" type="email" value={form.pec_email} onChange={(e) => setForm({ ...form, pec_email: e.target.value.toLowerCase() })} placeholder="agenzia@pec.it" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Numero RUI</Label>
              <Input data-testid="ob-rui" value={form.rui_number} onChange={(e) => setForm({ ...form, rui_number: e.target.value })} placeholder="A000123456" />
            </div>
            <div>
              <Label>IBAN</Label>
              <Input data-testid="ob-iban" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.replace(/\s/g, "").toUpperCase() })} placeholder="IT60 X054 2811 1010 0000 0123 456" />
            </div>
          </div>
          <div>
            <Label>Note aggiuntive (facoltative)</Label>
            <Textarea data-testid="ob-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Altre informazioni utili" />
          </div>

          {/* Documenti */}
          <div className="border-t border-slate-200 pt-4 mt-2">
            <Label className="text-sm font-semibold flex items-center gap-1.5"><FileText className="h-4 w-4 text-blue-600" /> Documenti (facoltativi ma consigliati)</Label>
            <p className="text-[11px] text-slate-500 mt-0.5 mb-3">Visura camerale e documento d&apos;identità del legale rappresentante. PDF/JPG/PNG, max 8 MB.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["visura", "id_card"].map((dt) => (
                <label key={dt} className="block cursor-pointer">
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    className="hidden"
                    data-testid={`ob-upload-${dt}`}
                    disabled={uploading === dt}
                    onChange={(e) => uploadDoc(e.target.files?.[0], dt)}
                  />
                  <div className="border-2 border-dashed border-slate-300 rounded-lg py-4 px-3 text-center hover:border-blue-400 hover:bg-blue-50/30 transition">
                    {uploading === dt ? (
                      <Loader2 className="h-5 w-5 mx-auto text-blue-600 animate-spin" />
                    ) : (
                      <Upload className="h-5 w-5 mx-auto text-slate-400" />
                    )}
                    <p className="text-xs mt-1 font-medium text-slate-700">{DOC_LABELS[dt]}</p>
                    <p className="text-[10px] text-slate-400">Clicca per caricare</p>
                  </div>
                </label>
              ))}
            </div>
            {docs.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {docs.map((d) => (
                  <li key={d.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-xs" data-testid={`ob-doc-${d.id}`}>
                    <div className="flex items-center gap-2 truncate">
                      <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      <span className="font-medium">{DOC_LABELS[d.doc_type] || "Documento"}</span>
                      <span className="text-slate-500 truncate">— {d.filename}</span>
                    </div>
                    <a href={d.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[11px] ml-2">apri</a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Button data-testid="ob-submit" onClick={submit} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Invia dati
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
